import { useQuery } from '@tanstack/react-query'
import { stepService } from '../api/stepService'
import { WAR_ROOM_QUERY_KEYS } from '../api/queryClient'
import type { Step, DependencyGraph } from '../types/session.types'

export interface StepsSelectData {
  all: Step[]
  byTeam: Record<string, Step[]>
  byId: Record<string, Step>
}

/**
 * All steps for a session, grouped by team client-side.
 * WS events trigger invalidation → auto-refetch.
 */
export function useSteps(sessionId: string) {
  return useQuery({
    queryKey: WAR_ROOM_QUERY_KEYS.steps(sessionId),
    queryFn: () => stepService.listBySession(sessionId).then((r) => r.data),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: !!sessionId,
    select: (steps: Step[]): StepsSelectData => ({
      all: steps,
      byTeam: groupStepsByTeam(steps),
      byId: Object.fromEntries(steps.map((s) => [s.id, s])),
    }),
  })
}

function groupStepsByTeam(steps: Step[]): Record<string, Step[]> {
  return steps.reduce<Record<string, Step[]>>((acc, step) => {
    const key = step.team_id
    if (!acc[key]) acc[key] = []
    acc[key].push(step)
    acc[key].sort((a, b) => a.order_index - b.order_index)
    return acc
  }, {})
}

/**
 * Dependency graph — cached 5 minutes (changes only on step definition edits).
 */
export function useDependencyGraph(sessionId: string) {
  return useQuery<DependencyGraph>({
    queryKey: WAR_ROOM_QUERY_KEYS.graph(sessionId),
    queryFn: () => stepService.getGraph(sessionId).then((r) => r.data),
    staleTime: 5 * 60_000,
    enabled: !!sessionId,
  })
}
